import { isNullish, isObject } from './type-guards';

/**
 * removes a property from an object
 * @param {string} prop the property key to remove
 * @param {object} obj the original object to remove the key from
 * @returns {object} a copy of the given object with the specified key removed
 */
export const removeProperty = <T, K extends keyof T>(
  prop: K,
  obj: T,
): Omit<T, K> => {
  const { [prop]: omit, ...rest } = obj satisfies T;
  return rest satisfies Omit<T, K>;
};

/**
 * removes an array of properties from a given object
 * @param {string[]} props array of property keys to remove from the object
 * @param {object} obj the original object to remove the keys from
 * @returns {object} a copy of the given object with the specified keys removed
 */
export const removeProperties = (props: string[], obj: object): object => {
  const entries = Object.entries(obj).filter(([key]) => !props.includes(key));
  return Object.fromEntries(entries);
};

/**
 * accepts two parameters and recursively compares them
 * @param valueA first value to be checked against
 * @param valueB second value to be checked against
 */
export const isEqual = (valueA: object | unknown, valueB: object | unknown) => {
  if (isObject(valueA) && isObject(valueB)) {
    const keysA = Object.keys(valueA);
    const keysB = Object.keys(valueB);
    if (keysA.length !== keysB.length) return false;
    if (keysB.every((key) => keysA.includes(key))) {
      return keysA.every((key) => {
        if (
          typeof valueA[key] === 'object' &&
          typeof valueB[key] === 'object'
        ) {
          return isEqual(valueA[key], valueB[key]);
        } else {
          return valueA[key] === valueB[key];
        }
      });
    }
  }
  return valueA === valueB;
};

/**
 * Checks if an object is empty.
 * @param obj - The object to check.
 * @returns Returns `true` if the object is empty, `false` otherwise.
 */
export function isEmpty(obj: unknown): boolean {
  return !!(isObject(obj) && Object.keys(obj).length === 0);
}

/**
 * Checks if every key in an object has a defined value that is not nullish.
 * @param {Object} obj - The object to check.
 * @returns {boolean} - Returns true if every key has a defined value that is not nullish, otherwise returns false.
 */
export function allValuesDefined(obj: unknown): boolean {
  return Boolean(
    isObject(obj) &&
      isEmpty(obj) === false &&
      Object.values(obj).every((value) => !isNullish(value)),
  );
}
